// MediaRecorder produces fragmented MP4. Finalize the sample tables and duration
// before download so editing applications can seek/import the saved recording.
// Encoded video/audio packets are copied locally, never uploaded or re-encoded.
export async function finalizeRecording(blob) {
  if (!/^video\/mp4(?:;|$)/i.test(blob.type)) return blob;
  const { Input, BlobSource, MP4, Output, BufferTarget, Mp4OutputFormat, Conversion } = await import('mediabunny');
  const input = new Input({ formats: [MP4], source: new BlobSource(blob) });
  try {
    const target = new BufferTarget();
    const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const conversion = await Conversion.init({ input, output, copy: { mode: 'forced' }, showWarnings: false });
    if (!conversion.isValid || conversion.discardedTracks.length) {
      throw new Error('Could not finalize every recording track.');
    }
    await conversion.execute();
    if (!target.buffer?.byteLength) throw new Error('The finalized recording was empty.');
    return new Blob([target.buffer], { type: 'video/mp4' });
  } finally {
    input.dispose();
  }
}

export function downloadRecordingBlob(blob, prefix) {
  const extension = /^video\/mp4(?:;|$)/i.test(blob.type) ? 'mp4' : 'webm';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  link.href = url;
  link.download = `${prefix}-${timestamp}.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Save the original if finalization fails, rather than losing a lesson recording.
export async function saveRecording(chunks, mimeType, prefix, onStatus = () => {}) {
  if (!chunks.length) return;
  const original = new Blob(chunks, { type: mimeType });
  try {
    onStatus('Preparing recording...');
    const finalized = await finalizeRecording(original);
    downloadRecordingBlob(finalized, prefix);
    onStatus('Recording saved.');
  } catch (error) {
    console.error('Recording finalization failed:', error);
    downloadRecordingBlob(original, `${prefix}-original`);
    onStatus('Could not prepare the editing-ready MP4. Your original recording was saved instead.');
  }
}
